// Date utility functions for HR ERP system
import { COMPANY_TIME_ZONE } from '../constants';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

// A real calendar date written as 'YYYY-MM-DD' (what the API sends for every DATE
// column). Such a value names a day, not an instant, so it must never be
// read through the browser's zone: new Date() parses it as UTC midnight, which local
// getters report as the PREVIOUS day anywhere west of UTC.
// Exact on purpose: V8's Date.parse rolls an impossible day over ('2026-02-30' parses
// as 2 March), so the parsed UTC day must round-trip to the very same string.
const isDateOnly = (value) => {
  if (typeof value !== 'string' || !DATE_ONLY.test(value)) return false;
  const ms = Date.parse(value);
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === value;
};

/**
 * Calculate business days between two date strings (inclusive, excludes Sat/Sun).
 * Shares the weekend rule with calcBusinessDays in server/repos/leaveRepo.js; update both
 * if it changes. The server ALSO excludes public holidays (attendance_holidays) and this
 * client-side preview does not yet, so on a week with a holiday the preview and the
 * over-quota warning can count one day more than the server records (follow-up).
 *
 * Two date-only strings are walked in UTC (their own calendar days), so the count is
 * the same in every browser zone. Any other input keeps the original local-time walk.
 */
export function calcBusinessDays(startDate, endDate) {
  if (isDateOnly(startDate) && isDateOnly(endDate)) {
    let count = 0;
    // Date.parse of a date-only string is UTC midnight, and UTC days are exactly DAY_MS.
    const end = Date.parse(endDate);
    for (let t = Date.parse(startDate); t <= end; t += DAY_MS) {
      const day = new Date(t).getUTCDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  }
  let count = 0;
  const cur = new Date(startDate);
  const end = new Date(endDate);
  while (cur <= end) {
    const day = cur.getDay();
    if (day !== 0 && day !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

const companyDayParts = new Intl.DateTimeFormat('en-US', {
  timeZone: COMPANY_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * Today, as the company's calendar day ('YYYY-MM-DD' in COMPANY_TIME_ZONE, Asia/Karachi).
 * The one "today" in the client, the same day the server's companyToday() uses: for date
 * defaults, an input's min/max, and anything judged against a DATE column. Never the
 * browser's own day: from 00:00 to 05:00 Karachi time a browser in the Americas is still
 * on the previous day, so a leave the form allowed for "today" was refused as backdated.
 * Never toISOString().slice(0, 10) either, which is the UTC day.
 * @param {Date} [now]
 * @returns {string} 'YYYY-MM-DD'
 */
export function companyToday(now = new Date()) {
  const part = Object.fromEntries(companyDayParts.formatToParts(now).map(({ type, value }) => [type, value]));
  return `${part.year}-${part.month}-${part.day}`;
}

/**
 * A 'YYYY-MM-DD' day moved by n calendar days (n may be negative). Calendar-only, so the
 * same in every zone; the server's addDays in utils/zonedTime.js does the same.
 * @param {string} dateStr 'YYYY-MM-DD'
 * @param {number} n
 * @returns {string} 'YYYY-MM-DD'
 */
export function addDays(dateStr, n) {
  return new Date(Date.parse(dateStr) + n * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The one formatter for a DATE column (hire_date, appraisal_date, effective_date,
 * expiry_date, a leave's start_date, ...). The API sends those as 'YYYY-MM-DD', which
 * names a calendar day, not an instant: it renders as that day in every browser zone.
 * new Date('2025-01-01') is UTC midnight, the 31st anywhere west of UTC, so a DATE
 * value must never be shown through new Date(x).toLocaleDateString().
 *
 * `locale` and `options` are toLocaleDateString's, so a page keeps its own format:
 * formatDateOnly(x) looks exactly like new Date(x).toLocaleDateString() did.
 * Anything that is not a real 'YYYY-MM-DD' day (a legacy ISO timestamp) falls back
 * to exactly new Date(x).toLocaleDateString(locale, options), i.e. as it rendered before.
 * @param {string|Date|null|undefined} value
 * @param {string|string[]} [locale]
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string} '' for a missing value
 */
export function formatDateOnly(value, locale, options) {
  if (value === null || value === undefined || value === '') return '';
  if (isDateOnly(value)) {
    return new Date(Date.parse(value)).toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
  }
  return new Date(value).toLocaleDateString(locale, options);
}

/**
 * Unique appraisal years, newest first, for the Appraisals year filter. A date-only
 * appraisal_date is its own calendar year (new Date('2025-01-01') is 2024 west of UTC).
 * @param {Array} appraisals - Array of appraisal objects
 * @returns {Array} Array of unique years sorted descending
 */
export function getUniqueYears(appraisals) {
  const years = [...new Set(appraisals.map(({ appraisal_date: date }) =>
    (isDateOnly(date) ? date.slice(0, 4) : new Date(date).getFullYear().toString())
  ))];
  return years.sort((a, b) => b - a);
}

/**
 * Format date and time for display
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(date, locale = 'en-US') {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString(locale);
}
